import React, { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Modal, ModalBody } from "reactstrap";
import { useMeCore } from "../../../cores/me";
import { WebsiteIcon } from "./WebsiteIcon";
import "./AiGenerateModal.scss";
import "./website-builder.scss";

export interface AiWebsiteFormData {
  companyName: string;
  description: string;
  designPreferences: string;
  logoFile: File | null;
  imageFiles: File[];
  brandColors: {
    primary: string;
    secondary: string;
    accent: string;
  };
}

export const BRAND_COLOR_PRESETS = [
  { id: "realestate", primary: "#0f5132", secondary: "#1a1a2e", accent: "#2d6a4f", labelKey: "app.websitePage.createAi.palette.realestate" },
  { id: "professional", primary: "#4f46e5", secondary: "#0f172a", accent: "#6366f1", labelKey: "app.websitePage.createAi.palette.professional" },
  { id: "warm", primary: "#b45309", secondary: "#292524", accent: "#d97706", labelKey: "app.websitePage.createAi.palette.warm" },
  { id: "elegant", primary: "#1e3a8a", secondary: "#0f172a", accent: "#3b82f6", labelKey: "app.websitePage.createAi.palette.elegant" },
];

interface Props {
  isOpen: boolean;
  isGenerating: boolean;
  onClose: () => void;
  onSubmit: (data: AiWebsiteFormData) => void;
}

export function CreateAiWebsiteModal({ isOpen, isGenerating, onClose, onSubmit }: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const imagesInputRef = useRef<HTMLInputElement>(null);

  const [companyName, setCompanyName] = useState(company?.name || "");
  const [description, setDescription] = useState("");
  const [designPreferences, setDesignPreferences] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [brandColors, setBrandColors] = useState({
    primary: "#0f5132",
    secondary: "#1a1a2e",
    accent: "#2d6a4f",
  });

  const handleGenerate = () => {
    if (!company?.id || !description.trim() || isGenerating) return;
    onSubmit({
      companyName,
      description: description.trim(),
      designPreferences: designPreferences.trim(),
      logoFile,
      imageFiles,
      brandColors,
    });
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} className="wp-ai-modal" size="lg" centered>
      <div className="wp-ai-modal__header">
        <div className="wp-ai-modal__header-icon">
          <WebsiteIcon icon="wand-magic-sparkles" />
        </div>
        <div>
          <h2>{intl.formatMessage({ id: "app.websitePage.createAi.title" })}</h2>
          <p>
            {isGenerating
              ? intl.formatMessage({ id: "app.websitePage.createAi.generatingSubtitle" })
              : intl.formatMessage({ id: "app.websitePage.createAi.subtitle" })}
          </p>
        </div>
        <button type="button" className="wp-ai-modal__close" onClick={handleClose}>
          <WebsiteIcon icon="times" />
        </button>
      </div>
      <ModalBody className="wp-ai-modal__body">
        {isGenerating ? (
          <div className="wp-ai-modal__waiting">
            <div className="wp-ai-modal__waiting-spinner">
              <WebsiteIcon icon="circle-notch" spin />
            </div>
            <h3>{intl.formatMessage({ id: "app.websitePage.createAi.waitingTitle" })}</h3>
            <p>{intl.formatMessage({ id: "app.websitePage.createAi.waitingDescription" })}</p>
            <div className="wp-ai-modal__waiting-note">
              <WebsiteIcon icon="gamepad" />
              <span>{intl.formatMessage({ id: "app.websitePage.createAi.dinoInvite" })}</span>
            </div>
            <div className="wp-ai-modal__waiting-safe">
              <WebsiteIcon icon="shield-alt" />
              <span>{intl.formatMessage({ id: "app.websitePage.createAi.closeSafeNotice" })}</span>
            </div>
          </div>
        ) : (
          <>
            <div className="wp-ai-modal__field">
              <label>{intl.formatMessage({ id: "app.websitePage.createAi.companyName" })}</label>
              <input className="wp-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
            </div>
            <div className="wp-ai-modal__field">
              <label>{intl.formatMessage({ id: "app.websitePage.createAi.description" })}</label>
              <textarea
                className="wp-input wp-ai-modal__textarea"
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={intl.formatMessage({ id: "app.websitePage.createAi.descriptionPlaceholder" })}
              />
            </div>
            <div className="wp-ai-modal__field">
              <label>{intl.formatMessage({ id: "app.websitePage.createAi.design" })}</label>
              <textarea
                className="wp-input wp-ai-modal__textarea"
                rows={3}
                value={designPreferences}
                onChange={(e) => setDesignPreferences(e.target.value)}
                placeholder={intl.formatMessage({ id: "app.websitePage.createAi.designPlaceholder" })}
              />
            </div>
            <div className="wp-ai-modal__field">
              <label>{intl.formatMessage({ id: "app.websitePage.createAi.brandColors" })}</label>
              <div className="wp-ai-modal__palette-presets">
                {BRAND_COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className={`wp-ai-modal__palette-preset ${
                      brandColors.primary === preset.primary ? "wp-ai-modal__palette-preset--active" : ""
                    }`}
                    onClick={() => setBrandColors({ primary: preset.primary, secondary: preset.secondary, accent: preset.accent })}
                    title={intl.formatMessage({ id: preset.labelKey })}
                  >
                    <span style={{ background: preset.primary }} />
                    <span style={{ background: preset.secondary }} />
                    <span style={{ background: preset.accent }} />
                  </button>
                ))}
              </div>
              <div className="wp-ai-modal__palette-custom">
                <div className="wp-ai-modal__palette-input">
                  <label>{intl.formatMessage({ id: "app.websitePage.createAi.colorPrimary" })}</label>
                  <input type="color" value={brandColors.primary} onChange={(e) => setBrandColors((c) => ({ ...c, primary: e.target.value }))} />
                </div>
                <div className="wp-ai-modal__palette-input">
                  <label>{intl.formatMessage({ id: "app.websitePage.createAi.colorSecondary" })}</label>
                  <input type="color" value={brandColors.secondary} onChange={(e) => setBrandColors((c) => ({ ...c, secondary: e.target.value }))} />
                </div>
                <div className="wp-ai-modal__palette-input">
                  <label>{intl.formatMessage({ id: "app.websitePage.createAi.colorAccent" })}</label>
                  <input type="color" value={brandColors.accent} onChange={(e) => setBrandColors((c) => ({ ...c, accent: e.target.value }))} />
                </div>
              </div>
              <div
                className="wp-ai-modal__palette-preview"
                style={{ background: `linear-gradient(135deg, ${brandColors.primary}, ${brandColors.accent})` }}
              >
                {intl.formatMessage({ id: "app.websitePage.createAi.palettePreview" })}
              </div>
            </div>
            <div className="wp-ai-modal__grid">
              <div className="wp-ai-modal__field">
                <label>{intl.formatMessage({ id: "app.websitePage.createAi.logo" })}</label>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                />
                <button type="button" className="wp-btn wp-btn--secondary wp-btn--sm" onClick={() => logoInputRef.current?.click()}>
                  <WebsiteIcon icon="upload" />
                  {logoFile ? logoFile.name : intl.formatMessage({ id: "app.websitePage.createAi.uploadLogo" })}
                </button>
              </div>
              <div className="wp-ai-modal__field">
                <label>{intl.formatMessage({ id: "app.websitePage.createAi.images" })}</label>
                <input
                  ref={imagesInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => setImageFiles(Array.from(e.target.files || []))}
                />
                <button type="button" className="wp-btn wp-btn--secondary wp-btn--sm" onClick={() => imagesInputRef.current?.click()}>
                  <WebsiteIcon icon="images" />
                  {imageFiles.length
                    ? intl.formatMessage({ id: "app.websitePage.createAi.imagesSelected" }, { count: imageFiles.length })
                    : intl.formatMessage({ id: "app.websitePage.createAi.uploadImages" })}
                </button>
              </div>
            </div>
            <p className="wp-ai-modal__credits">{intl.formatMessage({ id: "app.websitePage.createAi.creditsNotice" })}</p>
          </>
        )}
      </ModalBody>
      <div className="wp-ai-modal__footer">
        <button type="button" className="wp-btn wp-btn--secondary" onClick={handleClose}>
          {isGenerating
            ? intl.formatMessage({ id: "app.websitePage.createAi.closeAndContinue" })
            : intl.formatMessage({ id: "app.websitePage.cancel" })}
        </button>
        {!isGenerating && (
          <button
            type="button"
            className="wp-btn wp-btn--primary"
            onClick={handleGenerate}
            disabled={!description.trim()}
          >
            <WebsiteIcon icon="magic" />
            {intl.formatMessage({ id: "app.websitePage.createAi.generate" })}
          </button>
        )}
      </div>
    </Modal>
  );
}
